import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { InlineError } from "@shopify/polaris";
import { setIsModalxOpen } from "components/modalx/modalx.slice";
import { CustomField } from "types/CustomField";
import { Product } from "types/Product";
import DatePickerx from "features/datepicker/date-picker";
import { useAppDispatch } from "hooks";
import { fetchFileTemplateById } from "containers/files-template-form/files-template-form.service";
import { fetchFileDetails } from "containers/file/file.Slice";
import { fetchAuthorizedFileDetails } from "containers/file-authorized/authorized-file.Slice";
import { fetchById } from "services/upload-file.service";
import { fetchUserDetails } from "containers/user/user-container.slice";
import { fetchCategoryDetails } from "containers/document/category-tree/category-tree.Slice";
import { fetchFuelReportDetails } from "containers/fuel-report-view-container/fuel-report-container.slice";
import FileTemplateSelect from "components/file-template-select/file-template-select";
import { useSignerSelection } from "../hooks/useSignerSelection";
import { useCustomFields } from "../hooks/useCustomFields";
import CustomFieldsRenderer from "../components/CustomFieldsRenderer";
import DocumentFormShell from "../components/DocumentFormShell";
import {
  validateForm,
  getParentTemplateMismatchErrorKey,
  buildPayload,
  BuildPdfFormProps,
  normalizeBuildPdfTemplateId,
} from "../build-pdf-utils";
import { submitDocument, updateDocument } from "../build-pdf-api";
import {
  isLeaveSubject,
  resolveCanonicalLeaveTypeFromTemplate,
} from "features/leave-calendar/resolveLeaveTemplateClient";
import { MAX_LEAVE_DAYS } from "features/leave-calendar/leaveCalendar.constants";
import {
  isLeaveRangeTooLong,
  maxLeaveEndDate,
} from "features/leave-calendar/leaveCalendarDates";

export default function UserDocumentForm({
  customerId,
  isCustomerApproved = false,
  template,
  fileId,
  assignedUserId,
  id,
  subject,
  user,
  signType: signTypeProps,
  managerSigner,
  fuelReport,
  year,
  financeId,
  lockedTemplateId,
}: BuildPdfFormProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { companyId } = useParams();
  const dispatch = useAppDispatch();

  const [templateId, setTemplateId] = useState(lockedTemplateId || "");
  const [date, setDate] = useState<string>(
    new Date().toISOString().split("T")[0]
  );
  const [products, setProducts] = useState<Product[]>([]);
  const [productGroups, setProductGroups] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccessful, setIsSuccessful] = useState(false);
  const [isFailed, setIsFailed] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [fileTypeContext, setFileTypeContext] = useState("");
  const [templateIdByFetch, setTemplateIdByFetch] = useState("");
  const [fuelReportId, setFuelReportId] = useState<any>(null);
  const [dateFrom, setDateFrom] = useState<string>(
    new Date().toISOString().split("T")[0]
  );
  const [dateTo, setDateTo] = useState<string>(
    new Date().toISOString().split("T")[0]
  );

  const {
    selectedUser, selectedContactIds, signType, setSignType,
    selectedNonTechUser, selectedTabUser,
    hydrateSigners,
  } = useSignerSelection({
    signTypeProps,
    managerSigner,
    assignedUserId,
    isEditMode: !!id,
  });

  const {
    customFields, setCustomFields,
    templateEntity, setTemplateEntity,
    fileEntitiesMap,
    handleCustomFieldChange,
    transformCustomFields,
    setInitialCustomFieldsWhenEditLoaded,
    setInitialTemplateEntityWhenEditLoaded,
  } = useCustomFields({
    companyId,
    templateId,
    templateIdByFetch,
    isEditMode: !!id,
    onSignTypeDetected: setSignType,
  });

  const resolvedLeaveType = useMemo(
    () => resolveCanonicalLeaveTypeFromTemplate(templateEntity ?? null),
    [templateEntity]
  );

  useEffect(() => {
    if (lockedTemplateId && !id) setTemplateId(lockedTemplateId);
  }, [lockedTemplateId]);

  useEffect(() => {
    if (fuelReport) setFuelReportId(fuelReport);
  }, [fuelReport]);

  useEffect(() => {
    if (!id) return;
    async function fetchData() {
      try {
        const {
          date,
          templateId: templateId_,
          signType,
          filesTemplateCustomField,
          fileTypeContext,
          signersList,
          fuelreport,
        } = await fetchById(id, companyId);
        if (templateId_?.id) {
          const fetchedTemplateId = templateId_?.id;
          setTemplateId(fetchedTemplateId);
          setTemplateIdByFetch(fetchedTemplateId);

          const fileTemplateEntity = await fetchFileTemplateById(
            companyId,
            fetchedTemplateId
          );
          setTemplateEntity(fileTemplateEntity);
          const transformedFields = await transformCustomFields(
            filesTemplateCustomField?.customFields,
            fileTemplateEntity.customFields
          );
          const loadedCustomFields = fileTemplateEntity.customFields.map(
            (field: CustomField) => ({
              ...field,
              value:
                field.type === "file"
                  ? transformedFields[field.label] || []
                  : transformedFields[field.label] || "",
            })
          );
          setCustomFields(loadedCustomFields);
          setInitialCustomFieldsWhenEditLoaded(loadedCustomFields);
          setInitialTemplateEntityWhenEditLoaded(fileTemplateEntity);
          setProducts(filesTemplateCustomField?.products || []);
          setProductGroups(
            (filesTemplateCustomField as any)?.productGroups || []
          );
          setFuelReportId(fuelreport?.id || null);

          // Extract leave dates if present
          if (transformedFields.dateFrom) setDateFrom(transformedFields.dateFrom);
          if (transformedFields.dateTo) setDateTo(transformedFields.dateTo);
        }
        setSignType(signType);
        setDate(date);
        setFileTypeContext(fileTypeContext);
        hydrateSigners(signersList);
      } catch (_e) {
        // silently ignore
      }
    }
    fetchData();
  }, [id, companyId]);

  const parentTemplateId =
    !id && !lockedTemplateId
      ? normalizeBuildPdfTemplateId(template)
      : "";

  const isFormValid = () => {
    let validationErrors = validateForm({
      templateId,
      date,
      subject,
      selectedContactIds,
      signType,
      selectedUser,
      selectedNonTechUser,
      isSystemSource: false,
      isEditingSystemTemplate: false,
      isAutoBusinessTripDocument: false,
      customFields,
      products,
      productGroups,
    });
    const parentMismatch = getParentTemplateMismatchErrorKey({
      templateId,
      parentTemplateId,
    });
    if (parentMismatch) {
      validationErrors = { ...validationErrors, templateId: parentMismatch };
    }
    if (resolvedLeaveType) {
      if (dateFrom > dateTo) {
        validationErrors = {
          ...validationErrors,
          dateTo: "leave_calendar_start_after_end",
        };
      } else if (isLeaveRangeTooLong(dateFrom, dateTo)) {
        validationErrors = {
          ...validationErrors,
          dateTo: {
            key: "leave_calendar_max_days_error",
            params: { days: MAX_LEAVE_DAYS },
          },
        };
      }
    }
    const translatedErrors: { [key: string]: string } = {};
    for (const [key, val] of Object.entries(validationErrors)) {
      translatedErrors[key] =
        typeof val === "string" ? t(val) : t(val.key, val.params);
    }
    setErrors(translatedErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const handleFormSubmit = async () => {
    if (!isFormValid()) return;

    // Inject leave dates if applicable (explicit leave subject or legacy user + file type)
    const isLeave = !!resolvedLeaveType;
    const finalCustomFields = customFields.map((field) => ({ ...field }));
    if (isLeave) {
      const dateFromIdx = finalCustomFields.findIndex(f => f.label === 'dateFrom');
      if (dateFromIdx > -1) finalCustomFields[dateFromIdx].value = dateFrom;
      else finalCustomFields.push({ id: Date.now(), label: 'dateFrom', name: 'dateFrom', value: dateFrom, type: 'string', options: [], required: true });

      const dateToIdx = finalCustomFields.findIndex(f => f.label === 'dateTo');
      if (dateToIdx > -1) finalCustomFields[dateToIdx].value = dateTo;
      else finalCustomFields.push({ id: Date.now() + 1, label: 'dateTo', name: 'dateTo', value: dateTo, type: 'string', options: [], required: true });
    }

    const formData = buildPayload({
      date,
      templateId,
      customerId,
      customFields: finalCustomFields,
      products,
      productGroups,
      fileId,
      selectedContactIds,
      selectedUser,
      selectedNonTechUser,
      signType,
      selectedTabUser,
      fileTypeContext,
      user,
      orderId: null,
      fuelReportId,
      businessTripId: null,
      financeId,
    });

    if (!companyId) return;
    try {
      setIsFailed(false);
      setIsLoading(true);
      const action = id ? updateDocument : submitDocument;
      const fileEntity = await action(companyId, formData, id);
      const savedFileId = fileEntity?.id ?? fileEntity?.data?.id;
      if (fileId) dispatch(fetchFileDetails({ fileId, companyId }));
      if (savedFileId) {
        if (user) dispatch(fetchUserDetails({ userId: user, companyId }));
        dispatch(fetchCategoryDetails({ companyId, source: "profile" }));
        dispatch(fetchFileDetails({ fileId: savedFileId, companyId }));
        dispatch(fetchAuthorizedFileDetails({ fileId: savedFileId, companyId }));
        if (companyId && fuelReportId) {
          dispatch(
            fetchFuelReportDetails({ companyId, reportId: fuelReportId })
          );
        }
        if (
          templateEntity?.category &&
          savedFileId &&
          isLeaveSubject(subject || "") &&
          !fuelReportId
        )
          navigate(
            `/app/${companyId}/profile/files/${templateEntity.category}/${savedFileId}`
          );
        setIsLoading(false);
        setIsFailed(false);
        setIsSuccessful(true);
        setTimeout(() => {
          dispatch(setIsModalxOpen(false));
        }, 1500);
      } else {
        setIsFailed(true);
        setIsLoading(false);
      }
    } catch (_e) {
      setIsFailed(true);
      setIsLoading(false);
    }
  };

  return (
    <DocumentFormShell
      onSubmit={handleFormSubmit}
      isLoading={isLoading}
      isFailed={isFailed}
      isSuccessful={isSuccessful}
    >
      {!lockedTemplateId && !(id && !templateId) && (
        <>
          <FileTemplateSelect
            onChange={setTemplateId}
            value={templateId}
            isRequired={true}
            isCustomerApproved={isCustomerApproved}
            templateId={
              id ? "" : normalizeBuildPdfTemplateId(template) || templateIdByFetch
            }
            error={errors["templateId"]}
            subject={subject}
          />
        </>
      )}
      {templateId && (
        <DatePickerx
          disableDatesAfter={null}
          title={t("document_date")}
          isRequired={true}
          onChange={(value: any) => setDate(value.toISOString().split("T")[0])}
          selected={date}
          readOnly={isLeaveSubject(subject || "") || subject === "fuel"}
        />
      )}
      {templateId && resolvedLeaveType && (
        <div className="mb-4">
          <div className="flex gap-4">
            <div className="flex-1">
              <DatePickerx
                disableDatesAfter={null}
                title={t("date_from")}
                isRequired={true}
                onChange={(value: any) => {
                  const newKey = value.toISOString().split("T")[0];
                  setDateFrom(newKey);
                  if (dateTo && (dateTo < newKey || isLeaveRangeTooLong(newKey, dateTo))) {
                    setDateTo(newKey);
                  }
                }}
                selected={dateFrom}
              />
            </div>
            <div className="flex-1">
              <DatePickerx
                disableDatesAfter={dateFrom ? maxLeaveEndDate(dateFrom) : null}
                title={t("date_to")}
                isRequired={true}
                onChange={(value: any) => setDateTo(value.toISOString().split("T")[0])}
                selected={dateTo}
              />
            </div>
          </div>
          <InlineError message={errors.dateTo} fieldID="leave-date-to" />
        </div>
      )}
      <CustomFieldsRenderer
        customFields={customFields}
        errors={errors}
        onFieldChange={handleCustomFieldChange}
        products={products}
        productGroups={productGroups}
        setProducts={setProducts}
        setProductGroups={setProductGroups}
        orderId={null}
        templateId={templateId}
        customerId={customerId}
        fileEntitiesMap={fileEntitiesMap}
      />
    </DocumentFormShell>
  );
}
